import axios from 'axios';

const MAX_PAGE_SIZE = 100;
const REQUEST_TIMEOUT_MS = 20 * 1000;
const DECISION_TIMEOUT_MS = 2 * 60 * 1000;

function dataOf(response) {
  return response.data;
}

export function fetchOverview(academyId, signal) {
  return axios.get('/v1/grading/overview', { params: { academy: academyId }, signal, timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function fetchExam(examId, signal, { historyPage, historyPageSize } = {}) {
  return axios
    .get(`/v1/grading/exams/${examId}`, { params: { historyPage, historyPageSize }, signal, timeout: REQUEST_TIMEOUT_MS })
    .then(dataOf);
}

export function fetchExamSheets({ examId, filter, q, page }, signal) {
  return axios
    .get(`/v1/grading/exams/${examId}/sheets`, {
      params: { filter, q: q || undefined, page, pageSize: MAX_PAGE_SIZE },
      signal,
      timeout: REQUEST_TIMEOUT_MS
    })
    .then(dataOf);
}

export async function fetchAllExamSheets(examId, signal) {
  const rows = [];
  for (let page = 1; ; page += 1) {
    const response = await fetchExamSheets({ examId, filter: 'all', page }, signal);
    rows.push(...response.rows);
    if (response.rows.length === 0 || rows.length >= response.total) return rows;
  }
}

export function fetchSheet(answerSheetId, signal) {
  return axios.get(`/v1/grading/sheets/${answerSheetId}`, { signal, timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function confirmExamSheets(examId, answerSheetIds) {
  const body = answerSheetIds ? { answerSheetIds } : {};
  return axios.post(`/v1/grading/exams/${examId}/confirm`, body, { timeout: DECISION_TIMEOUT_MS }).then(dataOf);
}

export function confirmSheet(answerSheetId, note) {
  return axios.post(`/v1/grading/sheets/${answerSheetId}/confirm`, { note: note ?? null }, { timeout: DECISION_TIMEOUT_MS }).then(dataOf);
}

export function unconfirmSheets(examId, answerSheetIds, reason) {
  return axios.post(`/v1/grading/exams/${examId}/unconfirm`, { answerSheetIds, reason }, { timeout: DECISION_TIMEOUT_MS }).then(dataOf);
}

export function holdSheets(examId, answerSheetIds, reason) {
  return axios.post(`/v1/grading/exams/${examId}/holds`, { answerSheetIds, reason }, { timeout: DECISION_TIMEOUT_MS }).then(dataOf);
}

export function releaseHolds(examId, answerSheetIds) {
  return axios.delete(`/v1/grading/exams/${examId}/holds`, { data: { answerSheetIds }, timeout: DECISION_TIMEOUT_MS }).then(dataOf);
}

export function publishExam(examId, hold) {
  return axios.post(`/v1/grading/exams/${examId}/publish`, { hold: hold ?? null }, { timeout: DECISION_TIMEOUT_MS }).then(dataOf);
}

export function withdrawSheets(examId, { answerSheetIds, reason, confirmTitle }) {
  const body = answerSheetIds ? { answerSheetIds, reason } : { reason, confirmTitle };
  return axios.post(`/v1/grading/exams/${examId}/withdraw`, body, { timeout: DECISION_TIMEOUT_MS }).then(dataOf);
}

export function fetchRunJobs(runId, status, signal) {
  return axios
    .get(`/v1/grading/runs/${runId}/jobs`, { params: { status, pageSize: MAX_PAGE_SIZE }, signal, timeout: REQUEST_TIMEOUT_MS })
    .then(dataOf);
}

export function retryJob(jobId) {
  return axios.post(`/v1/grading/jobs/${jobId}/retry`, null, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function fetchNodes(signal) {
  return axios.get('/v1/grading/nodes', { params: { pageSize: MAX_PAGE_SIZE }, signal, timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function pauseNode(nodeId) {
  return axios.post(`/v1/grading/nodes/${nodeId}/pause`, null, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function resumeNode(nodeId) {
  return axios.post(`/v1/grading/nodes/${nodeId}/resume`, null, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function retireNode(nodeId) {
  return axios.delete(`/v1/grading/nodes/${nodeId}`, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function fetchEnrollmentToken(academyId, signal) {
  return axios.get(`/v1/grading/academies/${academyId}/enrollment-token`, { signal, timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function issueEnrollmentToken(academyId) {
  return axios.post(`/v1/grading/academies/${academyId}/enrollment-token`, null, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function revokeEnrollmentToken(academyId) {
  return axios.delete(`/v1/grading/academies/${academyId}/enrollment-token`, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function fetchProfiles(signal) {
  return axios.get('/v1/grading/profiles', { params: { pageSize: MAX_PAGE_SIZE }, signal, timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function fetchAcademyPolicy(academyId, signal) {
  return axios.get(`/v1/grading/academies/${academyId}/policy`, { signal, timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function saveAcademyPolicy(academyId, policy) {
  return axios.put(`/v1/grading/academies/${academyId}/policy`, policy, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function fetchExamCandidates({ academyId, q, page, pageSize }, signal) {
  return axios
    .get('/v1/grading/exam-candidates', {
      params: { academy: academyId, q: q || undefined, page, pageSize },
      signal,
      timeout: REQUEST_TIMEOUT_MS
    })
    .then(dataOf);
}

export function fetchExamPolicy(examId, signal) {
  return axios.get(`/v1/grading/policies/${examId}`, { signal, timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function enableExam(examId, profileId) {
  return axios.post(`/v1/grading/exams/${examId}/enable`, { profileId }, { timeout: DECISION_TIMEOUT_MS }).then(dataOf);
}

export function fetchExamStimuli(examId, all, signal) {
  return axios
    .get(`/v1/grading/exams/${examId}/stimuli`, { params: { all: all ? '1' : undefined }, signal, timeout: REQUEST_TIMEOUT_MS })
    .then(dataOf);
}

export function saveStimuli(questionId, stimuli) {
  return axios.put(`/v1/grading/questions/${questionId}/stimuli`, { stimuli }, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function previewQuestionRegrade(questionId, examId) {
  return axios
    .post(
      `/v1/grading/questions/${questionId}/regrade`,
      { examId, reason: { reasonCode: 'recheck', note: null }, dryRun: true },
      { timeout: REQUEST_TIMEOUT_MS }
    )
    .then(dataOf);
}

export function regradeQuestion(questionId, { examId, reason }) {
  return axios
    .post(`/v1/grading/questions/${questionId}/regrade`, { examId, reason, dryRun: false }, { timeout: DECISION_TIMEOUT_MS })
    .then(dataOf);
}

export function fetchPronunciation(examId, signal) {
  return axios.get(`/v1/grading/exams/${examId}/pronunciation`, { signal, timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function fetchAgreement({ academyId, source, examIds, cut }, signal) {
  const params = { academy: academyId, source, cut };
  if (examIds.length > 0) {
    params.examIds = examIds.join(',');
  }
  return axios.get('/v1/grading/agreement', { params, signal, timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function fetchReviewGroups({ academyId, examId }, signal) {
  return axios
    .get('/v1/grading/reviews/groups', { params: { academy: academyId, examId }, signal, timeout: REQUEST_TIMEOUT_MS })
    .then(dataOf);
}

export function claimReviews(examId, groupKey) {
  const body = groupKey ? { examId, groupKey } : { examId };
  return axios.post('/v1/grading/reviews/claim', body, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function releaseReviews(reviewIds, { keepalive = false } = {}) {
  if (reviewIds.length === 0) {
    return;
  }
  if (!keepalive) {
    axios.post('/v1/grading/reviews/release', { reviewIds }, { timeout: REQUEST_TIMEOUT_MS }).catch(() => {});
    return;
  }
  const headers = { 'Content-Type': 'application/json' };
  const authorization = axios.defaults.headers.common.Authorization;
  if (authorization) {
    headers.Authorization = authorization;
  }
  fetch(`${axios.defaults.baseURL}/v1/grading/reviews/release`, {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    headers,
    body: JSON.stringify({ reviewIds })
  }).catch(() => {});
}

export function fetchReviewCard(reviewId) {
  return axios.get(`/v1/grading/reviews/${reviewId}/card`, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function decideReview(reviewId, decision) {
  return axios.post(`/v1/grading/reviews/${reviewId}/decide`, decision, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function reviseReview(reviewId, decision) {
  return axios.post(`/v1/grading/reviews/${reviewId}/revise`, decision, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function revealReview(reviewId, blindScore) {
  return axios.post(`/v1/grading/reviews/${reviewId}/reveal`, { blindScore }, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function fetchNearSilent({ examId, page, pageSize }, signal) {
  return axios
    .get('/v1/grading/reviews/near-silent', { params: { examId, page, pageSize }, signal, timeout: REQUEST_TIMEOUT_MS })
    .then(dataOf);
}

export function keepNearSilent(examId, items) {
  return axios.post('/v1/grading/reviews/keep-near-silent', { examId, items }, { timeout: REQUEST_TIMEOUT_MS }).then(dataOf);
}

export function adjustSheetItem(answerSheetId, answerId, adjustment) {
  return axios
    .post(`/v1/grading/sheets/${answerSheetId}/items/${answerId}/adjust`, adjustment, { timeout: REQUEST_TIMEOUT_MS })
    .then(dataOf);
}

export function problemOf(error) {
  const data = error?.response?.data;
  return data && typeof data === 'object' ? data : null;
}

export function problemDetailOf(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.length > 0) {
    return detail;
  }
  if (error?.response) {
    return `서버가 오류(${error.response.status})를 돌려줬습니다.`;
  }
  return '서버에 연결하지 못했습니다.';
}

export function isNotFound(error) {
  return error?.response?.status === 404;
}

export function isCanceled(error) {
  return axios.isCancel(error);
}
